import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Film, FileText, Plus, Radio, Sparkles, X } from 'lucide-react';
import { AICreateEntry } from '../aicreate/components/AICreateEntry';
import { logRender } from '@/utils/debugLoop';
import { TemplateSection } from '../components/TemplateSection';
import { TemplateCard } from '../components/TemplateCard';
import {
  CONTENT_TEMPLATES,
  DOCUMENT_TEMPLATES,
  TWINBOOST_TEMPLATES,
  type PresetKey as TemplatePresetKey,
} from '../data/templates';

const Editor2 = React.lazy(() => import('./Editor2'));

type PresetKey = '1080x1080' | '1080x1920' | '1280x720' | '1920x1080';

// Default styles when theme is disabled
const DEFAULT_STYLES = {
  bg: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
  surface: 'rgba(31, 41, 55, 0.5)',
  border: 'rgba(75, 85, 99, 0.5)',
  text: '#ffffff',
  textDim: '#9ca3af',
};

export default function Studio() {
  logRender('Studio');

  const navigate = useNavigate();
  const useEditorPalette = true;

  // ===== Editor state =====
  const [editorOpen, setEditorOpen] = useState(false);
  const [initialPreset, setInitialPreset] = useState<PresetKey>('1920x1080');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Scroll lock body while editor is open
  useEffect(() => {
    if (!editorOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const target = e.target as HTMLElement;
        if (target && typeof target.closest === 'function' && target.closest('input, textarea, select, [contenteditable="true"]')) {
          return;
        }
        const hasOpenDialog = document.querySelector('[role="dialog"]') || document.querySelector('.modal-open');
        if (hasOpenDialog) return;
      }
    };

    document.body.style.overflow = 'hidden';
    document.documentElement.classList.remove('allow-scroll');
    document.body.classList.remove('allow-scroll');
    document.getElementById('root')?.classList.remove('allow-scroll');

    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      document.documentElement.classList.add('allow-scroll');
      document.body.classList.add('allow-scroll');
      document.getElementById('root')?.classList.add('allow-scroll');
    };
  }, [editorOpen]);

  // ===== Actions =====
  function startVideoProject(preset?: PresetKey) {
    setInitialPreset(preset || '1920x1080');
    setEditorOpen(true);
    setShowCreateModal(false);
  }

  function startDocumentProject() {
    setShowCreateModal(false);
    navigate('/studio/docs/new');
  }

  return (
    <div
      data-testid="page-studio"
      className="min-h-screen w-full transition-[background] duration-200"
      style={{ background: useEditorPalette ? 'var(--vb-editor-bg)' : DEFAULT_STYLES.bg }}
    >
      {/* ===== Main content - Template Hub ===== */}
      <main className="flex flex-col items-center px-4 py-12">
        {/* Logo / Title */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent mb-4">
            ViewsBoost Studio
          </h1>
          <p className="text-lg max-w-md mx-auto" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>
            Create stunning videos, graphics, and documents with our powerful editor
          </p>
        </div>

        {/* Primary Action Buttons: Video | Document */}
        <div className="flex flex-col sm:flex-row gap-6 mb-12">
          {/* Video Button */}
          <button
            onClick={() => startVideoProject()}
            className="group relative flex flex-col items-center gap-4 p-8 rounded-2xl hover:border-blue-500 transition-all duration-300 min-w-[200px]"
            style={{
              background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            <div className="w-20 h-20 rounded-2xl bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
              <Film className="w-10 h-10 text-blue-400 group-hover:text-blue-300" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>Video</h3>
              <p className="text-sm" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>Create engaging video content</p>
            </div>
          </button>

          {/* Document Button */}
          <button
            onClick={startDocumentProject}
            className="group relative flex flex-col items-center gap-4 p-8 rounded-2xl hover:border-purple-500 transition-all duration-300 min-w-[200px]"
            style={{
              background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            <div className="w-20 h-20 rounded-2xl bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
              <FileText className="w-10 h-10 text-purple-400 group-hover:text-purple-300" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>Document</h3>
              <p className="text-sm" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>Design graphics and documents</p>
            </div>
          </button>

          {/* TwinBoost AI Button */}
          <button
            onClick={() => navigate('/studio/twinboost')}
            className="group relative flex flex-col items-center gap-4 p-8 rounded-2xl hover:border-amber-500 transition-all duration-300 min-w-[200px]"
            style={{
              background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
              <Sparkles className="w-10 h-10 text-amber-400 group-hover:text-amber-300" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>TwinBoost</h3>
              <p className="text-sm" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>AI-powered video avatars</p>
            </div>
          </button>

          {/* AI Create Button */}
          <AICreateEntry
            onClick={() => navigate('/studio/ai-create')}
            themeStyles={{
              surface: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
              border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
              text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
              textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
            }}
          />
        </div>

        {/* Create Button (opens modal with choices) */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold hover:from-yellow-300 hover:to-orange-400 transition-all shadow-lg hover:shadow-xl hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          Create
        </button>

        <button
          type="button"
          onClick={() => navigate('/studio/replays')}
          className="mt-4 inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-emerald-400 hover:text-white"
          style={{
            color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
            borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
          }}
        >
          <Radio className="h-4 w-4 text-emerald-400" />
          Past Streams & Replays
        </button>

        {/* Quick Start Sizes (for Video) */}
        <div className="mt-16 text-center">
          <h3 className="text-sm font-medium uppercase tracking-wide mb-4" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>Quick Start Video Sizes</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { key: '1080x1080' as PresetKey, label: 'Square', sub: '1080×1080' },
              { key: '1080x1920' as PresetKey, label: 'Story/Shorts', sub: '1080×1920' },
              { key: '1280x720' as PresetKey, label: 'Thumbnail', sub: '1280×720' },
              { key: '1920x1080' as PresetKey, label: 'Banner', sub: '1920×1080' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => startVideoProject(s.key)}
                className="px-4 py-2 rounded-lg transition-colors text-sm hover:opacity-90"
                style={{
                  background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
                }}
              >
                <span style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>{s.label}</span>
                <span className="ml-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>({s.sub})</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== Template Hub Sections ===== */}
        <div className="mt-16 w-full max-w-5xl space-y-10">
          {/* Content Editor Templates */}
          <TemplateSection
            title="Content Editor Templates"
            themeStyles={{
              text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
              textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
              border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            {CONTENT_TEMPLATES.map((template) => (
              <TemplateCard
                key={template.id}
                id={template.id}
                name={template.name}
                badge={template.badge}
                gradient={template.gradient}
                description={template.description}
                accentColor="blue"
                onClick={() => startVideoProject(template.preset as PresetKey)}
                themeStyles={{
                  surface: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
                  border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
                  text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
                  textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
                }}
              />
            ))}
          </TemplateSection>

          {/* Document Templates */}
          <TemplateSection
            title="Document Templates"
            themeStyles={{
              text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
              textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
              border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            {DOCUMENT_TEMPLATES.map((template) => (
              <TemplateCard
                key={template.id}
                id={template.id}
                name={template.name}
                badge={template.badge}
                gradient={template.gradient}
                description={template.description}
                accentColor="purple"
                onClick={() => {
                  // Store config in sessionStorage for document creation
                  const config = {
                    preset: template.pageSize,
                    orientation: template.orientation,
                    templateId: template.id,
                  };
                  sessionStorage.setItem('newDocConfig', JSON.stringify(config));
                  navigate('/studio/docs/new');
                }}
                themeStyles={{
                  surface: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
                  border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
                  text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
                  textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
                }}
              />
            ))}
          </TemplateSection>

          {/* TwinBoost AI Video Templates */}
          <TemplateSection
            title="TwinBoost AI Video"
            themeStyles={{
              text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
              textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
              border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            {TWINBOOST_TEMPLATES.map((template) => (
              <TemplateCard
                key={template.id}
                id={template.id}
                name={template.name}
                badge={template.badge}
                gradient={template.gradient}
                description={template.description}
                accentColor="amber"
                onClick={() => navigate(`/studio/twinboost?template=${template.id}`)}
                themeStyles={{
                  surface: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
                  border: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
                  text: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text,
                  textDim: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim,
                }}
              />
            ))}
          </TemplateSection>
        </div>
      </main>

      {/* ===== Create Modal (Video/Document choice) ===== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div
            className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl overflow-hidden"
            style={{
              background: useEditorPalette ? 'var(--vb-editor-surface)' : DEFAULT_STYLES.surface,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border,
            }}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between p-5"
              style={{ borderBottom: `1px solid ${useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border}` }}
            >
              <h2 className="text-xl font-bold" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>What do you want to create?</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Options */}
            <div className="p-5 grid grid-cols-2 gap-4">
              {/* Video Option */}
              <button
                onClick={() => startVideoProject()}
                className="group flex flex-col items-center gap-3 p-6 rounded-xl border-2 hover:border-blue-500 transition-all"
                style={{ borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border }}
              >
                <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                  <Film className="w-7 h-7 text-blue-400 group-hover:text-white" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>Video</h3>
                  <p className="text-xs mt-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>Engaging video content</p>
                </div>
              </button>

              {/* Document Option */}
              <button
                onClick={startDocumentProject}
                className="group flex flex-col items-center gap-3 p-6 rounded-xl border-2 hover:border-purple-500 transition-all"
                style={{ borderColor: useEditorPalette ? 'var(--vb-editor-border)' : DEFAULT_STYLES.border }}
              >
                <div className="w-14 h-14 rounded-full bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                  <FileText className="w-7 h-7 text-purple-400 group-hover:text-white" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold" style={{ color: useEditorPalette ? 'var(--vb-editor-text)' : DEFAULT_STYLES.text }}>Document</h3>
                  <p className="text-xs mt-1" style={{ color: useEditorPalette ? 'var(--vb-editor-text-dim)' : DEFAULT_STYLES.textDim }}>Graphics & documents</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Editor overlay ===== */}
      {editorOpen && (
        <div
          className="fixed inset-0 z-50"
          style={{ background: useEditorPalette ? 'var(--vb-editor-bg)' : '#000000' }}
        >
          <div className="absolute inset-0 z-[55]">
            <React.Suspense
              fallback={
                <div
                  className="absolute inset-0"
                  style={{ background: useEditorPalette ? 'var(--vb-editor-bg)' : '#000000' }}
                />
              }
            >
              <Editor2
                initialPreset={initialPreset}
                onClose={() => setEditorOpen(false)}
              />
            </React.Suspense>
          </div>
        </div>
      )}
    </div>
  );
}
